import {
  ICreateOrUpdateInboundData,
  IEvaluationImagesConfiguration,
  IEvaluationsAssetItem,
  IFileUploadData,
  IInboundSecureFile,
  IInventoryScanOption,
  ITransferTrayInfo,
  TEvaluationMediaAction,
  TEvaluationMediaType,
  TEvaluationType,
  TPackagePodType,
  TPodType,
  TSortOrder,
} from '@northladder/services';
import {
  IApiError,
  TTextLocalized,
  TTranslatedText,
} from '@northladder/utilities';

export interface IPendencyItem {
  /**
   * The color of the pendency item and should be aliased to `as TThemeColor`.
   */
  color: unknown;
  fromHour: number;
  isGreaterThan: boolean;
  isSelected?: boolean;
  key: string;
  time: string;
  toHour?: number;
  value: string;
}

/**
 * `IInboundItem` is data structure of each item in InboundList.
 */
export interface IInboundItem {
  assetName: string;
  courierProvider: string;
  devices: number;
  id: string;
  updatedAt: number;
  updatedBy: string;
}

/**
 * `IInboundListData` is data structure of `TInboundListResponse`.
 */
export interface IInboundListDataRaw {
  categoryList: IInboundItem[];
}

export interface IInboundStoreInfo {
  address: string;
  employeeId?: string;
  mobileNumber?: string;
  name: string;
  pendingPackageCount?: number;
  type?: string;
}

/**
 * `IInboundPodItem` is data structure of each item in InboundPodsSummaryItem.
 */
export interface IFileUploadDataDepreated {
  key: string; // TxKeyPath TODO: Find the replacement for TxKeyPath in the comps
  value: number;
}

/**
 * `IInboundPodsSummaryItem` is data structure of each item in InboundPodsSummary.
 */
export interface IInboundPodsSummaryItem {
  items?: IInboundPodItem[];
  podTitle?: {
    key: string; // TxKeyPath TODO: Find the replacement for TxKeyPath in the comp
    value: number;
  };
  podType: TPodType;
}

/**
 * `IInboundSummary` is data structure of InboundSummary.
 */
export interface IInboundSummary {
  duration: string;
  remainingItems: number;
}

/**
 * `IFieldFilter` is data structure of FieldFilter.
 */
export interface IFieldFilter {
  key: string;
  value: string;
}

/**
 * `IKeywordFilter` is data structure of KeywordFilter.
 */
export interface IKeywordFilter {
  key: string;
  value: string;
}

export interface IPodPackage {
  packageCount: number;
  podType: TPodType;
}

export interface IPackageRecordDetails {
  createdAt: number;
  id: string;
  status: string;
  transactionId: string;
  updatedAt: number;
  updatedBy: string;
}

export interface IInboundDetails {
  packages: {
    itemsScanned: number;
    packages: IPodPackage[];
  };
  packageSummary: IPackageRecordDetails;
}

export interface TDriverDetails {
  firstName: string;
  lastName: string;
  courierProvider: string;
  driverIdentification: string;
}

export interface IInboundHistoryDetails extends IPackageRecordDetails {
  type: string;
  blackBoxTransactionPackageId?: string;
}

/**
 * `IInboundPodItem` is data structure of each item in InboundPodsSummaryItem.
 */
export interface IInboundPodItem {
  key: string; // TxKeyPath TODO: Find the replacement for TxKeyPath in the comps
  value: number;
}

export interface IPackageDetails {
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  assetSerialNumber?: string;
  awbNumber?: string;
  barCode: string;
  boxNumber: string;
  courierProvider?: string;
  podType: TPodType;
  updatedDate: number;
}

/**
 * `INlStaff` defines the structure of the Nl Staff data.
 */
export interface INorthladderStaff {
  _id: string;
  cityName?: string;
  countryName?: string;
  departmentName?: string;
  email: string;
  employeeCode?: string;
  firstName: string;
  lastName: string;
  userId?: string;
}

/**
 * `INlStaffResponse` is the response type of NL Staff list api
 */
export interface INlStaffResponse {
  status: boolean;
  message: string;
  data: INorthladderStaff[];
}

/**
 * Interface for fetching the NL Staff list url.
 */
export interface IFetchNlStaffUrlOptions {
  count: number;
  page: number;
  search: string;
}

/**
 * Interface for fetching the Inbound Search List url.
 */
export interface IFetchSearchListUrlOptions {
  endDate?: string | null;
  filterKey?: string;
  limit: number;
  page?: number;
  recordType: TInboundTabGroupType;
  search?: string;
  searchFields?: string[];
  searchKeywords?: string[];
  showOnly?: 'OPEN' | 'CLOSED';
  sort?: TSortOrder;
  startDate?: string | null;
}

export enum InboundType {
  LOCAL = 'LOCAL', // ?? Stock picked up from UAE store
  IN_HOUSE_TRANSFER = 'IN_HOUSE_TRANSFER', // ?? non sold device
  FRESH_INBOUND_ROW = 'FRESH_INBOUND_ROW', // ?? Inbound from RGCC & ROW
  BUYBACKS = 'BUYBACKS', // ?? Devices returned by the Dealer
  NDR = 'NDR', // ?? Non  Delivered Returns
}

/**
 * This interface defines the structure of the Inbound Item.
 */
export interface IInboundAssetItem {
  _id: string;
  courierPersonFullName: string;
  courierProvider: string;
  inventoryCount: number;
  inventoryTransactionId: string;
  transactionStatus: 'OPEN' | 'CLOSED';
  updatedBy: string;
  updatedDate: number;
}

/**
 * This interface defines the structure of the Inbound List Items Data.
 */
export interface IInboundListItemsData {
  inbounds: IInboundAssetItem[];
  total: number;
}

/**
 * `TInboundListResponse` is type which defines the response structure
 * of InboundList API.
 */
export type TInboundListResponse = {
  data: IInboundListItemsData;
  message: string;
  status: boolean;
};

/**
 * This interface `IInboundSearchFilterOption` defines the structure of the Inbound Filter Item.
 */
export interface IInboundSearchFilterOptionRaw {
  key: string;
  value: TTextLocalized;
}

/**
 * This interface `IInboundSearchFilterOptionRaw` defines the structure of the
 * Inbound Filter Option Data.
 */
export interface IInboundSearchFilterOptionDataRaw {
  searchFields: IInboundSearchFilterOptionRaw[];
  searchKeywords: IInboundSearchFilterOptionRaw[];
}

/**
 * This interface `IInboundSearchFilterOption` defines the structure of the
 * Inbound Filter Formatted Option.
 */
export interface IInboundSearchFilterOption {
  key: string;
  label: TTextLocalized;
}

/**
 * `IInboundSearchFilterOptionResponse` is type which defines the response structure
 * of Inbound List Filter Options API.
 */
export type IInboundSearchFilterOptionResponse = {
  data: IInboundSearchFilterOptionDataRaw;
  message: string;
  status: boolean;
};

/**
 * This interface `IInboundSearchFilterOptionFormatted` defines the structure
 * of the Inbound Filter Option Formatted.
 */
export interface IInboundSearchFilterOptionFormatted {
  searchFields: IInboundSearchFilterOption[];
  searchKeywords: IInboundSearchFilterOption[];
}

/**
 * This interface `IInboundCourierProvider` defines the structure of the Inbound Courier Provider.
 */
export interface IInboundCourierProvider {
  checked?: boolean;
  id: string;
  imgUrl: string;
  key: string;
  label: string;
  trackingUrl: string;
}

/**
 * `IInboundCourierProviderResponse` is type which defines the response structure
 * of Inbound Courier Providers List API.
 */
export type IInboundCourierProviderResponse = {
  data: IInboundCourierProvider[];
  message: string;
  status: boolean;
};

/**
 * This interface `IInboundTransactionItem` defines the structure of the Inbound
 * Transaction Type Item.
 */
export interface IInboundTransactionType {
  id: string;
  key: TInboundType;
  value: TTextLocalized;
}

/**
 * `IInboundTransactionTypesResponse` is type which defines the response structure
 * of Inbound Types List API.
 */
export type IInboundTransactionTypesResponse = {
  data: IInboundTransactionType[];
  message: string;
  status: boolean;
};

/**
 * This type `TInboundType` defines the enum for the Inbound Transaction Type Value.
 */
export type TInboundType =
  | 'BUYBACKS'
  | 'FRESH_INBOUND_ROW'
  | 'IN_HOUSE_TRANSFER'
  | 'LOCAL'
  | 'NDR';

/**
 * `ICreateOrUpdateInboundPayload` is the payload structure for creating or updating an Inbound.
 */
export interface ICreateOrUpdateInboundPayload {
  id?: string;
  inboundType: string;
  courierType: string;
  courierPersonFullName?: string;
  gatePass: string;
  courierPersonIdentityImages?: IInboundSecureFile[];
  nlStaffName?: string;
  nlStaffId?: string;
  inboundRemarks?: string;
  courierSignature?: IInboundSecureFile[];
}

/** `
 * `ICreateOrUpdateInboundResponse` is the response structure for creating or updating an Inbound.
 */
export type ICreateOrUpdateInboundResponse = {
  data: ICreateOrUpdateInboundData;
  message: string;
  status: boolean;
};

/**
 * Interface representing the ageing information of the Inbounds.
 */
export interface IAgeing {
  count: number;
  fromHour: number;
  key: string;
  toHour?: number;
}

/**
 * Interface representing the oldest inbound information.
 */
export interface IOldestInbound {
  _id: string;
  inboundType: string;
  inboundDate: number;
  inventoryTransactionId: string;
  transactionStatus: string;
  createdBy: string;
  createdDate: number;
}

/**
 * Interface representing the inbounds statistics information.
 */
export interface IStatistics {
  packages: number;
  podNumber: string;
  podType: TPodType;
  transactions: number;
  trays: number;
  zone: string;
}

/**
 * Interface representing the open inbound statics information.
 */
export interface IOpenInboundInfo {
  openInbounds: number;
  oldestInbound: IOldestInbound;
}

export interface IOldestMissingInventory {
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  barCode: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  courierProvider: string;
  createdBy: string;
  createdUserId: string;
  createdDate: number;
  groupId: string;
  groupName: string;
  imeiOrSerialNumber: string;
  inboundDate: number;
  inboundType: string;
  inventoryTransactionId: string;
  statusHistory: IStatusHistory[];
  tenantId: string;
  updatedBy: string;
  updatedUserId: string;
  updatedDate: number;
}

/**
 * Interface representing the inbound statistics response.
 */
export interface IInboundStaticsResponse {
  statistics: IStatistics[];
  openInboundInfo: IOpenInboundInfo;
  ageing: IAgeing[];
  missingInventoriesCount: number;
  oldestMissingInventory: IOldestMissingInventory | null;
}

/**
 * Type representing the raw inbound statics response.
 */
export type IInboundResponseRaw = {
  data: IInboundStaticsResponse;
  message: string;
  status: boolean;
};

/**
 * Interface representing the details of a pod.
 */
export interface IPodDetails {
  _id: string;
  podNumber: string;
  podName: string;
  podDescription: string;
  podType: TPodType;
  status: boolean;
  createdBy: string;
  createdUserId: string;
  createdDate: number;
  updatedBy: string;
  updatedUserId: string;
  updatedDate: number;
  tenantId: string;
}

/**
 * Interface representing an inventory item within a pod.
 */
export interface IPodInventoryItem {
  _id: string;
  appraisalCode?: string;
  assetId?: string;
  assetName?: string;
  awbNumber?: string;
  barCode?: string;
  boxNumber: string;
  boxScannedBy: string;
  boxScannedDate: string;
  boxScannedUserId: string;
  brandId: string;
  brandName: string;
  courierProvider: string;
  createdBy: string;
  createdDate: number;
  imeiOrSerialNumber: string;
  inboundDate: number;
  inboundScanCode: string;
  inboundType: string;
  inventoryId?: string;
  inventoryLocation: string;
  inventoryTransactionId: string;
  packageScannedType: string;
  podName: string;
  podNumber: string;
  podType: TPodType;
  transactionStatus: string;
  trayId: string;
  trayNumber: string;
  updatedBy: string;
  updatedDate: number;
}

/**
 * Interface representing the age of inventory items.
 */
export interface IInventoryAge {
  count: number;
  fromHour: number;
  key: string;
  toHour?: number;
}

/**
 * Interface representing the Tray details.
 */
export interface ITray {
  appraisalCount: string;
  transferStatus?: string;
  trayId?: string;
  trayNumber: string;
}

/**
 * Interface representing the response data for pod details and aging info.
 */
export interface IPodDataResponse {
  pod: IPodDetails;
  age: IInventoryAge[];
  tray: ITray[];
}

/**
 * Interface representing the raw response data for pod details API.
 */
export interface IPodDetailsDataResponseRaw {
  data: IPodDataResponse;
  message: string;
  status: boolean;
}

/**
 * Interface representing the response data for pod details and total packages count.
 */
export interface IPodInventoryListResponse {
  podInventories: IPodInventoryItem[];
  total: number;
}

/**
 * Interface representing the raw response data for pod inventory list API.
 */
export interface IPodInventoryListResponseRaw {
  data: IPodInventoryListResponse;
  message: string;
  status: boolean;
}

export interface ICourierItem {
  _id: string;
  fileName: string;
  filePath: string;
  thumbnailPath: string;
}

/**
 * `IInboundItemDetail` is data structure of Inbound detail object.
 */
export interface IInboundItemDetail {
  _id: string;
  courierPersonFullName: string;
  courierPersonIdentityImages: ICourierItem[];
  courierSignature: ICourierItem[];
  courierType: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  devices?: IDevice[];
  gatePass: string;
  inboundClosedDate: number;
  inboundDate: number;
  inboundRemarks: string;
  inboundType: string;
  inventoryTransactionId: string;
  nlStaffId: string;
  nlStaffName: string;
  tenantId: string;
  transactionStatus: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

/**
 * `IDevice` is data structure of each device in Inbound detail.
 */
export interface IDevice {
  _id: string;
  appraisalCode?: string;
  assetId?: string;
  assetName?: string;
  barCode?: string;
  boxNumber: string;
  imeiOrSerialNumber: string;
  inboundBarcode?: string;
  inboundScanCode?: string;
  inboundType: string;
  inventoryTransactionId: string;
  isInventoryInBlackBox: boolean;
  packageScannedType: string;
  podName: string;
  podNumber: string;
  podType: TPodType;
  tenantId: string;
  transactionStatus: string;
  trayNumber: string;
  updatedBy: string;
  updatedDate: number;
}

/**
 * `IPackage` is data structure of each package in Inbound detail.
 */
export interface IPackage {
  podNumber: string;
  podType: TPodType;
  pods: number;
  zone: string;
}

/**
 * `IInboundDetailResponse` is data structure of Inbound detail response.
 */
export interface IInboundDetailResponse {
  inbound: IInboundItemDetail;
  packages?: IPackage[];
}

/**
 * `IInboundDetailResponseRaw` is data structure of Inbound detail response raw.
 */
export interface IInboundDetailResponseRaw {
  data: IInboundDetailResponse;
  message: string;
  status: boolean;
}

/**
 * `ICloseInboundTransactionPayload` is data structure of Close Inbound Transaction Payload.
 */
export interface ICloseInboundTransactionPayload {
  inventoryTransactionId: string;
  courierSignature: IInboundSecureFile[];
}

export interface ITransactionCreated {
  appraisalCode: string;
  createdBy: string;
  createdDate: number;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  updatedBy: string;
  updatedDate: number;
}

export interface IPackageAddedTransaction {
  appraisalCode: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

export interface ITransactionClosed {
  appraisalCode: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  updatedBy: string;
  updatedDate: number;
}

export interface IAdditionalInfo {
  currentTrayNumber: string;
  currentPod?: TPodType;
  previousPod?: TPodType;
  previousTrayNumber: string;
  invEditFieldName?: TFieldToUpdateType;
}

export interface IPodSwitchedTransaction {
  additionalInfo?: IAdditionalInfo;
  appraisalCode: string;
  description: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

/**
 * Extra information for the edit history item.
 */
export interface IAdditionalHistoryInfo {
  invEditCurrentValue: string | null;
  invEditFieldName?: string | null;
  invEditPreviousValue: string | null;
}

export interface IPackageEditedTransaction {
  additionalInfo?: IAdditionalHistoryInfo | null;
  appraisalCode: string;
  description: string;
  fieldUpdated?: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

export interface IPackageToBlackBoxTransaction {
  appraisalCode: string;
  description: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

export interface IPackageFromBlackBoxTransaction {
  appraisalCode: string;
  description: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

export interface IPackageUnknownTransaction {
  appraisalCode: string;
  description: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

export interface ICourierInfoUpdatedTransaction {
  appraisalCode: string;
  description: string;
  historyType: HistoryType;
  inventoryTransactionId: string;
  podNumber?: string;
  podType: TPodType;
  remarks?: string;
  transactionStatus: string;
  trayNumber?: string;
  updatedBy: string;
  updatedDate: number;
}

/**
 * `TInboundHistory` is data structure of Inbound History Item.
 */
export type TInboundHistory =
  | ITransactionCreated
  | IPackageAddedTransaction
  | ITransactionClosed
  | IPodSwitchedTransaction
  | IPackageEditedTransaction
  | IPackageToBlackBoxTransaction
  | IPackageFromBlackBoxTransaction
  | IPackageUnknownTransaction
  | ICourierInfoUpdatedTransaction;

/**
 * The Payload for fetching the Inbound history list.
 */
export interface IGetInboundHistoryListPayload {
  enabled: boolean;
  historyType?: HistoryType;
  inventoryTransactionId: string;
  limit: number;
  page?: number;
}

/**
 * `ICloseInboundTransactionResponse` is data structure of Inbound History Response.
 */
export interface IInboundHistoryListResponse {
  total: number;
  historyList: TInboundHistory[];
  inboundDetails: IInboundItemDetail | null;
  filter: HistoryType[];
}

/**
 * `ICloseInboundTransactionResponseRaw` is data structure of Inbound History Item Response Raw.
 */
export interface IInboundHistoryListResponseRaw {
  data: IInboundHistoryListResponse;
  message: string;
  status: boolean;
}

/**
 * `IGetPodInventoryListPayload` is the payload structure for fetching the pod inventory list.
 */
export interface IGetPodInventoryListPayload {
  filterKey?: string;
  inventoryTransactionId?: string;
  limit: number;
  page?: number;
  podIdentifier: string;
  trayNumber?: string;
  zone: string;
  recordType: TInboundTabGroupType;
  searchText?: string;
}

/**
 * This is the interface for the pod details query parameters.
 */
export interface IFetchPodDetailUrlOptions {
  ageFilter?: string;
  inventoryTransactionId?: string;
  podIdentifier: string;
  recordType: TInboundTabGroupType;
  searchText?: string;
  trayNumber?: string;
  zone: string;
}

export interface ISourcePackageData {
  data: IPackageDetails[];
  title: string;
}

export interface ISourceDetailsData {
  storeInfo: IInboundStoreInfo;
  storePackageData?: ISourcePackageData[];
  total: number;
}

export interface IRawSourceDetailsData {
  nlStaff: INorthladderStaff;
  store: {
    address1: string;
    address2: string;
    city: string;
    country: string;
    name: string;
  };
}
export interface ISourceInventoryListRaw {
  inventories: IPackageDetails[];
  location: string;
  total: number;
}

export interface ISourceDetailsRaw {
  source: IRawSourceDetailsData;
  total: number;
  multiLocationInventories: ISourceInventoryListRaw[];
}

export interface ISourceDetailsResponseRaw {
  data: ISourceDetailsRaw;
  message: string;
  status: boolean;
}

export interface ISourceDetailsParams {
  sourceId: string;
  page: number;
  limit: number;
}

export enum HistoryType {
  INBOUND_CREATED = 'INBOUND_CREATED', // OPEN
  INBOUND_CLOSED = 'INBOUND_CLOSED', // CLOSED
  INBOUND_COURIER_INFO_UPDATED = 'INBOUND_COURIER_INFO_UPDATED', // OPEN
  INBOUND_INVENTORY_ADDED = 'INBOUND_INVENTORY_ADDED', // ADDED
  INBOUND_INVENTORY_MOVED_TO_BLACK_BOX = 'INBOUND_INVENTORY_MOVED_TO_BLACK_BOX', // REMOVED
  INBOUND_INVENTORY_MOVED_TO_TRANSACTION_FROM_BLACK_BOX = 'INBOUND_INVENTORY_MOVED_TO_TRANSACTION_FROM_BLACK_BOX', // ADDED
  INBOUND_ARCHIVED = 'INBOUND_ARCHIVED', // CLOSED
  POD_SWITCH = 'POD_SWITCH',
  INBOUND_INVENTORY_UPDATED = 'INBOUND_INVENTORY_UPDATED',
  UNKNOWN = 'UNKNOWN',
}

export type TInboundTabGroupType = 'ACTIVE' | 'HISTORY';

export type TInboundNDRPackageTabStatus = 'PENDING' | 'RECEIVED';

/**
 * This type `TFieldToUpdateType` defines the enum for the Update Field Type Value.
 */
export type TFieldToUpdateType = 'BAR_CODE' | 'BOX_NUMBER';

/**
 * This interface `IFieldToUpdateType` defines the structure of the Field to Update Type.
 */
export interface IFieldToUpdateType {
  id: string;
  dbKey: string;
  key: TFieldToUpdateType;
  label: string;
}

/**
 * `IFieldToUpdateTypesResponseRaw` is type which defines the response structure
 * of Field Types List API.
 */
export type IFieldToUpdateTypesResponseRaw = {
  data: IFieldToUpdateType[];
  message: string;
  status: boolean;
};

/**
 * `IUpdateInboundPackagePayload` is the payload structure for updating
 * an Inbound package details.
 */
export interface IUpdateInboundPackagePayload {
  inboundId: string;
  requestBody: {
    fieldKey: string;
    remark: string;
    updateValue: string;
  };
}

/**
 * `IUpdateInboundPackageResponseRaw` is the raw response structure for updating
 * an Inbound package details.
 */
export interface IUpdateInboundPackageResponseRaw {
  acknowledged: boolean;
}

/** `
 * `IUpdateInboundPackageResponse` is the response structure for updating
 * an Inbound package details.
 */
export type IUpdateInboundPackageResponse = {
  data: IUpdateInboundPackageResponseRaw;
  message: string;
  status: boolean;
};

/**
 * `BulkScanInventoryFilter` defines the possible filter values for bulk scanning inventories.
 */
export type BulkScanInventoryFilter = 'MISSING' | 'RECEIVED' | 'PENDING';

/**
 * `IScannedNDRInventory` represents a scanned inventory item with its details and status.
 */
export interface IScannedNDRInventory {
  _id: string;
  appraisalCode: string;
  assetLabel: string;
  assetName: string;
  barCode: string;
  inventoryTransactionId: string | null;
  missing: boolean;
  pending: boolean;
  podType: TPodType;
  received: boolean;
  scannedBy?: string;
  serialNumber: string;
  trayNumber?: string;
  unIdentified: boolean;
}

/**
 * `IBulkScanInventoriesPayload` defines the payload structure for bulk scanning
 * inventories request.
 */
export interface IBulkScanInventoriesPayload {
  filter: BulkScanInventoryFilter;
  inventoryTransactionId: string;
  limit: number;
  page?: number;
}

/**
 * `IBulkScanInventoriesResponse` defines the response data structure for bulk scanning inventories.
 */
export interface IBulkScanInventoriesResponse {
  inventories: IScannedNDRInventory[];
  missingInventoriesCount: number;
  pendingInventoriesCount: number;
  receivedInventoriesCount: number;
  total: number;
  unIdentifiedInventoriesCount: number;
}

/**
 * `IBulkScanInventoriesResponseRaw` defines the raw API response structure for
 * bulk scanning inventories,
 * including the data, message and status.
 */
export interface IBulkScanInventoriesResponseRaw {
  data: IBulkScanInventoriesResponse;
  message: string;
  status: boolean;
}

export interface IMissingInventoryPayload {
  appraisalCode: string;
  inventoryTransactionId: string;
}

export interface IMissingInventoryResponse {
  data: null;
  message: string;
  status: boolean;
}

/**
 * The Payload for fetching the Missing Inventory history list.
 */
export interface IGetMissingInventoryListPayload {
  inventoryTransactionId?: string;
  limit: number;
  page?: number;
  search: string;
}

/**
 * `IStatusHistory` defines the structure of a status history item inside the
 * missing inventory record.
 */
export interface IStatusHistory {
  changedBy: string;
  notes?: string;
  status: string;
  timestamp: number;
}

/**
 * `IMissingInventoryListResponse` is data structure of Missing Inventory List Response.
 */
export interface IMissingInventoryRecord {
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  barCode: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  courierProvider?: string;
  createdBy?: string;
  createdDate?: number;
  createdUserId?: string;
  groupId: string;
  groupName: string;
  imeiOrSerialNumber?: string;
  inboundDate: number;
  inboundType: TInboundType;
  inventoryTransactionId: string;
  status: string;
  statusHistory?: IStatusHistory[];
  tenantId: string;
  updatedBy?: string;
  updatedDate?: number;
  updatedUserId?: string;
}

export interface IMissingInventoryListData {
  records: IMissingInventoryRecord[];
  total: number;
}

/**
 * `IMissingInventoryListResponse` defines the response data structure for
 * missing inventory list.
 */
export interface IMissingInventoryListResponse {
  data: IMissingInventoryListData;
  message: string;
  status: boolean;
}

/**
 * Interface for fetching the NL Staff list url.
 */
export interface IFetchEvaluationListParams {
  count: number;
  page: number;
  search?: string;
  status: TEvaluationType;
}

/**
 * This interface defines the structure of the Evaluations List Items Data.
 */
export interface IEvaluationsListItemsData {
  evaluations: IEvaluationsAssetItem[];
  total: number;
}

/**
 * `IEvaluationsListResponse` is type which defines the response structure
 * of Evaluations API.
 */
export interface IEvaluationsListResponse {
  data: IEvaluationsListItemsData;
  message: string;
  status: boolean;
}

/**
 * This interface defines the query parameters for fetching the evaluations
 * images configuration.
 */
export interface IFetchEvaluationImagesConfigurationParams {
  categoryType: string;
}

export interface IEvaluationImageConfigurationResponse {
  data: IEvaluationImagesConfiguration[];
  message: string;
  status: boolean;
}

/**
 * Params for scanning a package for initiating an evaluation.
 */
export interface IScanPackageForEvaluationPayload {
  barcode: string;
}

export interface IScanPackageForEvaluationData {
  sessionId: string;
}

/**
 * Response for scanning a package for initiating an evaluation.
 */
export interface IScanPackageForEvaluationResponse {
  data: IScanPackageForEvaluationData;
  message: string;
  status: boolean;
}

/**
 * Payload for scanning a port for mapping a package to port.
 */
export interface IScanPortForCreatingEvaluationPayload {
  port: string;
  sessionId: string;
}

/**
 * Payload for scanning an evaluated appraisal.
 */
export interface IScanEvaluatedAppraisalPayload {
  code: string;
}

/**
 * Response for scanning a port for mapping a package to port.
 */
export interface IScanPortForCreatingEvaluationResponse {
  data: IEvaluationsAssetItem;
  message: string;
  status: boolean;
}

/**
 * Payload for scanning an port for an existing evaluation.
 */
export interface IScanPortForExistingEvaluationPayload {
  port: string;
}

/**
 * Response for scanning a port for an existing evaluation.
 */
export interface IScanPortForExistingEvaluationResponse {
  data: IEvaluationsAssetItem;
  message: string;
  status: boolean;
}

/**
 * Payload for adding/deleting an image to/from an evaluation.
 */
export interface IAddDeleteImageToEvaluationPayload {
  action: TEvaluationMediaAction;
  evaluationId: string;
  items: IFileUploadData;
  mediaType: TEvaluationMediaType;
}

/**
 * Payload for closing an evaluation.
 */
export interface ICloseEvaluationPayload {
  evaluationId: string;
}

export interface ICloseEvaluationResponseData {
  evaluationId: string;
  appraisalCode: string;
}

/**
 * Response for closing an evaluation.
 */
export interface ICloseEvaluationResponse {
  data: ICloseEvaluationResponseData;
  message: string;
  status: boolean;
}

/**
 * Params to fetch evaluation details of done evaluation.
 */
export interface IFetchEvaluationDetailsParams {
  evaluationId: string;
}

export interface IEvaluationDetailsResponse {
  data: IEvaluationsAssetItem;
  message: string;
  status: boolean;
}

export interface IInventoryScanOptionsResponse {
  data: IInventoryScanOption[];
  message: string;
  status: boolean;
}

/**
 * Payload format for scanning a package to remove.
 */
export interface IScanPackageToRemovePayload {
  inventoryScanCode: string;
  packageScannedType: string;
  remarks?: string;
}

/**
 * Response data format we expect from scanning a package to remove.
 */
export interface IScanPackageToRemoveData {
  podInfo: {
    podNumber: string;
    podName: string;
    podType: TPodType;
    podDescription: string;
    packageCount?: number;
  };
  trayInfo?: ITransferTrayInfo;
  packageIdentifier: string;
  blackBoxInventoryCount?: number;
  inventoryData: {
    _id: string;
    boxNumber: string;
    boxScannedBy: string;
    boxScannedUserId: string;
    boxScannedDate: string;
    packageScannedType: string;
    inventoryTransactionId: string;
    tenantId: string;
    inboundBarcode: string;
    inboundDate: number;
    inboundType: string;
    transactionStatus: string;
    barCode: string;
    appraisalCode: string;
    assetId: string;
    assetName: string;
    groupId: string;
    groupName: string;
    brandId: string;
    brandName: string;
    categoryId: string;
    categoryName: string;
    imeiOrSerialNumber: string;
    sourceOfSupply: {
      storeId: string;
      storeName: string;
      type: string;
      name: string;
      address: string;
      contactNumber: string;
      nlStaffName: string;
      nlStaffId: string;
      _id: string;
    };
    podId: string;
    podNumber: string;
    podName: string;
    podType: TPodType;
    podInventoryEntryTimeStamp: number;
    trayId: string;
    trayNumber: string;
    trayEntryTimeStamp: number;
    isInventoryInBlackBox: boolean;
    createdBy: string;
    createdUserId: string;
    createdDate: number;
    updatedBy: string;
    updatedUserId: string;
    updatedDate: number;
  };
}

/**
 * Response format for scanning a package to remove.
 */
export interface IScanPackageToRemoveResponse {
  status: boolean;
  data: IScanPackageToRemoveData;
  message: string;
}

/**
 * Payload format for scanning a tray.
 */
export interface IScanTrayPayload {
  /**
   * Identifier of the inventory, in case of adding to black box, it will be
   * _id of the inventory info we get from scanning the package. And in case of
   * adding a package to new inventory, it will be the transaction id of the new
   * inbound.
   */
  inventoryIdentifier: string;
  /**
   * The key we get after we successfully scan a package to either remove it or
   * add it to a new inventory.
   */
  packageIdentifier: string;
  /**
   * The tray number that was scanned, this is the same tray where the package
   * was kept.
   */
  trayNumber: string;
}

/**
 * Data that we expect after scanning a tray.
 */
export interface IScanTrayData {
  invalidTray: boolean;
  packageIdentifier: string;
  trayInfo: ITransferTrayInfo;
  trayScanned: boolean;
}

/**
 * Response format after scanning a tray.
 */
export interface IScanTrayResponse {
  status: boolean;
  data: IScanTrayData;
  message: string;
}

export interface IScanPodPayload {
  inventoryIdentifier: string;
  packageIdentifier: string;
  podNumber: string;
}

export interface IScanPodResponse {
  message: string;
  status: boolean;
}

export interface IGetInventoryInfo {
  inventoryTransactionId: string;
}

export interface IGetInventoryInfoPayload {
  inventoryTransactionId: string;
  limit: number;
  page?: number;
  recordType: TInboundTabGroupType;
  searchText?: string;
}

export interface IInventoryInfo {
  _id: string;
  boxNumber: string;
  courierProvider?: string;
  // boxScannedBy: string;
  // boxScannedUserId: string;
  // boxScannedDate: string;
  packageScannedType: string;
  inventoryTransactionId: string;
  tenantId: string;
  inboundBarcode: string;
  // inboundDate: number;
  inboundType: string;
  transactionStatus: string;
  barCode: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  // groupId: string;
  // groupName: string;
  // brandId: string;
  // brandName: string;
  // categoryId: string;
  // categoryName: string;
  imeiOrSerialNumber: string;
  inboundScanCode?: string;
  // sourceOfSupply: {
  //   storeId: string;
  //   storeName: string;
  //   type: string;
  //   name: string;
  //   address: string;
  //   contactNumber: string;
  //   nlStaffName: string;
  //   nlStaffId: string;
  //   _id: string;
  // };
  // podId: string;
  podNumber: string;
  podName: string;
  podType: TPodType;
  // podInventoryEntryTimeStamp: number;
  // trayId: string;
  trayNumber: string;
  // trayEntryTimeStamp: number;
  isInventoryInBlackBox: boolean;
  // createdBy: string;
  // createdUserId: string;
  // createdDate: number;
  updatedBy: string;
  // updatedUserId: string;
  updatedDate: number;
}

/**
 * This interface represents the inventory information data.
 * TODO: Please update the interface as per need.
 */
export interface IInventoryInfoData {
  inbound: {
    _id: string;
    inboundType: string;
    courierType: string;
    courierPersonFullName: string;
    courierProvider: string;
    gatePass: string;
    courierPersonIdentityImages: {
      fileName: string;
      filePath: string;
      thumbnailPath: string;
      _id: string;
    }[];
    nlStaffName: string;
    nlStaffId: string;
    inboundClosedDate: number;
    inboundRemarks: string;
    inboundDate: number;
    inventoryTransactionId: string;
    tenantId: string;
    transactionStatus: 'OPEN' | 'CLOSED';
    courierSignature: {
      fileName: string;
      filePath: string;
      thumbnailPath: string;
      _id: string;
    }[];
    createdBy: string;
    createdUserId: string;
    createdDate: number;
    updatedBy: string;
    updatedUserId: string;
    updatedDate: number;
  };
  inboundInventories: {
    inventories: IInventoryInfo[];
    total: number;
    pods: IPackage[];
  };
  missingInventoryCount: number;
}

/**
 * This interface represents the response format for inventory information.
 */
export interface IInventoryInfoResponse {
  status: boolean;
  data: IInventoryInfoData;
  message: string;
}

/**
 * Payload format for scanning a package to add it to an inbound.
 */
export interface IScanPackageToAddPayload {
  boxIdentifier?: string;
  boxNumber?: string;
  inventoryScanCode: string;
  inventoryTransactionId: string;
  packageScannedType: string;
}

/**
 * Raw response data format we expect from scanning a package to add.
 */
export interface IScanPackageToAddDataRaw {
  boxIdentifier?: string;
  inventoryData: {
    appraisalCode?: string;
    courierProvider?: string;
    barCode?: string;
    assetName?: string;
  };
  multipleInventories?: boolean;
  trayInfo?: ITransferTrayInfo;
  packageIdentifier: string;
  podInfo: {
    podNumber: string;
    podName: string;
    podType: TPodType;
    podDescription: string;
  };
}

/**
 * Response data format we expect from scanning a package to add.
 */
export interface IScanPackageToAddData {
  boxIdentifier?: string;
  inventoryData: {
    appraisalCode: string;
    courierProvider: string;
    barCode: string;
    assetName: string;
  };
  multipleInventories?: boolean;
  trayInfo?: ITransferTrayInfo;
  packageIdentifier: string;
  podInfo: {
    podNumber: string;
    podName: string;
    podType: TPodType;
    podDescription: string;
  };
}

/**
 * Response format for scanning a package to add.
 */
export interface IScanPackageToAddResponse {
  data: IScanPackageToAddDataRaw;
  message: string;
  status: boolean;
}
/// Package summary types

export interface IBlackBoxHistory {
  boxId: string;
  addedBy: string;
  dateAdded: string;
  dateRemoved: string;
  removedBy: string;
  locationBarCode: string;
  recordedOn: string;
  recordedBy: string;
}

export interface IInboundTransaction {
  transactionId: string;
  inboundType: string;
  transferPod: string;
  recordedOn: string;
  recordedBy: string;
}

export interface IEvaluationTransaction {
  transactionId: string;
  incomingPod: string;
  outboundPod: string;
  grade: string;
  evaluatedOn: string;
  evaluatedBy: string;
}

export interface IInventoryTransaction {
  grade: string;
  transactionId: string;
  storage: string;
  locationBarCode: string;
  recordedOn: string;
  recordedBy: string;
}

export interface IDispatchTransaction {
  stackId: string;
  stackCreatedDate: string;
  pickedBy: string;
  pickLocation: string;
  pickDate: string;
  dateDispatched: string;
  dateToOutbound: string;
  outboundPod: string;
}

export interface IOutboundTransaction {
  transactionId: string;
  to: string;
  courier: string;
  driver: string;
  dateDispatched: string;
}

export interface IInventoryCount {
  stockDate: string;
  stockTakeId: string;
  stockTakeLocation: string;
  takenBy: string;
}

export interface IPackageSummary {
  deviceName: string;
  dealSummary: IDealSummary;
  currentLocation: ICurrentLocationRaw;
  history: {
    blackBox: IBlackBoxHistory;
    inboundTransaction: IInboundTransaction;
    evaluationTransaction: IEvaluationTransaction;
    inventoryTransaction: IInventoryTransaction;
    dispatchTransaction: IDispatchTransaction;
    outboundTransaction: IOutboundTransaction;
    inventoryCounts: IInventoryCount[];
  };
}

export interface IBlackBoxInventoryQueryParams {
  enabled?: boolean;
  inventoryTransactionId?: string;
  limit: number;
  page?: number;
  search?: string;
}

export interface IBlackBoxInventoryQuery {
  payload: IBlackBoxInventoryQueryParams;
  enabled?: boolean;
}

export interface IBlackboxInventoriesData {
  blackBoxInventories: IInventoryInfo[];
  total: number;
}

/**
 * This interface represents the response format for blackbox inventory
 * information.
 */
export interface IBlackboxInventoryInfoResponse {
  status: boolean;
  data: IBlackboxInventoriesData;
  message: string;
}

/**
 * Payload format for scanning a package to remove.
 */
export interface IScanPackageToMoveFromBlackboxPayload {
  inventoryTransactionId: string;
  inventoryScanCode: string;
  packageScannedType: string;
  remarks?: string;
}

/**
 * Response data format we expect from scanning a package to remove.
 */
export interface IScanPackageToMoveFromBlackboxData {
  podInfo: {
    podNumber: string;
    podName: string;
    podType: TPodType;
    podDescription: string;
  };
  trayInfo?: ITransferTrayInfo;
  packageIdentifier: string;
  inventoryData: {
    _id: string;
    boxNumber: string;
    // boxScannedBy: string;
    // boxScannedUserId: string;
    // boxScannedDate: string;
    // packageScannedType: string;
    inventoryTransactionId: string;
    tenantId: string;
    inboundBarcode: string;
    inboundDate: number;
    inboundType: string;
    transactionStatus: string;
    barCode: string;
    appraisalCode: string;
    assetId: string;
    assetName: string;
    // groupId: string;
    // groupName: string;
    // brandId: string;
    // brandName: string;
    // categoryId: string;
    // categoryName: string;
    imeiOrSerialNumber: string;
    // sourceOfSupply: {
    //   storeId: string;
    //   storeName: string;
    //   type: string;
    //   name: string;
    //   address: string;
    //   contactNumber: string;
    //   nlStaffName: string;
    //   nlStaffId: string;
    //   _id: string;
    // };
    podId: string;
    podNumber: string;
    podName: string;
    podType: TPodType;
    // podInventoryEntryTimeStamp: number;
    trayId: string;
    trayNumber: string;
    trayEntryTimeStamp: number;
    isInventoryInBlackBox: boolean;
    // createdBy: string;
    // createdUserId: string;
    // createdDate: number;
    // updatedBy: string;
    // updatedUserId: string;
    updatedDate: number;
  };
}

/**
 * Response format for scanning a package to remove.
 */
export interface IScanPackageToMoveFromBlackboxResponse {
  status: boolean;
  data: IScanPackageToRemoveData;
  message: string;
}

/// Pendency report types
export type TReportFilterLabel = {
  en: string;
  ar: string;
};

export interface IReportAging {
  count: number;
  fromHour: number;
  key: string;
  toHour?: number;
}

interface IPendencyDateItem {
  key: string;
  value: number;
  i18n: TReportFilterLabel;
}

export interface IPendencyDateValue {
  aging: IReportAging[];
  today: IPendencyDateItem[];
  yesterday: IPendencyDateItem[];
}

export interface IPendencyReportData {
  key: string;
  value: IPendencyDateValue;
}

export interface IPendencyReportUpdatingData {
  updatingData: boolean;
}

export interface IPendencyReportResponse {
  data: IPendencyReportData[];
  message: string;
  status: boolean;
  errorCode?: string;
}

// Pendency filter options types
export interface IPendencyFilterOptionItem {
  key: string;
  value: TReportFilterLabel;
}

export interface IPendencyFilterOptionsData {
  key: string;
  value: IPendencyFilterOptionItem[];
}

export interface IPendencyFilterOptionsResponse {
  data: IPendencyFilterOptionsData[];
  status: boolean;
}

// Search result listing screen payload types
export interface ISearchPackageResultPayload {
  endDate?: string;
  limit?: number;
  location?: string;
  page?: number;
  search?: string;
  searchFields?: string[];
  searchKeywords?: string[];
  sort?: string;
  startDate?: string;
}

export enum TLocationType {
  INBOUND = 'INBOUND',
  EVALUATION = 'EVALUATION',
  INVENTORY = 'INVENTORY',
  BLACKBOX = 'BLACKBOX',
}

/// Search result list screen types
export interface ISearchResultPackage {
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  imeiOrSerialNumber: string;
  podName: string;
  trayNumber: string;
  podType: TPodType;
  awbNumber: string;
  barCode: string;
}

export interface IMultiLocationInventory {
  location: TLocationType;
  inventories: ISearchResultPackage[];
  total: number;
}

export interface IPackageSearchResultData {
  isMultipleLocation: boolean;
  multiLocationInventories: IMultiLocationInventory[];
  inventories: ISearchResultPackage[];
  total: number;
}

export interface IPackageSearchResultResponse {
  status: boolean;
  data: IPackageSearchResultData;
}

export interface IResultPackageSummaryPayload {
  inventoryId?: string;
  appraisalCode?: string;
}

export interface IMissingPackageDetailsPayload {
  missingInventoryId: string;
}

/// Inbound Package Details types
export interface IHistoryDetailsRaw {
  inboundType?: string;
  podName: string;
  podNumber: string;
  recordedAt: number;
  recordedBy: string;
  recordedUserId: string;
  transactionId?: string;
  trayNumber: string;
}

export enum InventoryLocationType {
  INBOUND = 'INBOUND',
  EVALUATION = 'EVALUATION',
  BLACK_BOX = 'BLACK_BOX',
  HAND_OVER = 'HAND_OVER',
  OUTBOUND = 'OUTBOUND',
}

export interface IInventoryHistoryRaw {
  key: InventoryLocationType;
  value: IHistoryDetailsRaw;
}

export interface ICurrentLocationRaw {
  inventoryLocation: InventoryLocationType;
  podName: string;
  podNumber: string;
  recordedAt: number;
  recordedBy: string;
  recordedUserId: string;
  trayNumber: string;
}

export interface IDealSummary {
  assetName: string;
  dealDate: number;
  dealId: string;
  imei: string;
  imeiOrSerialNumber: string;
}

export interface IInboundPackageDetailDataRaw {
  currentLocation: ICurrentLocationRaw;
  dealSummary: IDealSummary;
  inventoryHistory: IInventoryHistoryRaw[];
}

export interface IInboundPackageDetailResponse {
  data: IInboundPackageDetailDataRaw;
  status: boolean;
}

/// formatted Inbound Package Details types
export interface IHistoryDetails {
  inboundType?: string;
  podName: string;
  podNumber: string;
  recordedAt: number;
  recordedBy: string;
  recordedUserId: string;
  transactionId?: string;
  trayNumber: string;
}

export interface IInventoryHistory {
  label: string;
  key: InventoryLocationType;
  value: IHistoryDetails;
}

// TODO: Revise this type with Renji as to why there were 2 current locations
export interface ICurrentLocation {
  inventoryLocation: string;
  location: string;
  locationBarCode: string;
  podName: string;
  podNumber: string;
  recordedAt: number;
  recordedBy: string;
  recordedOn: string;
  recordedUserId: string;
  storage: string;
  trayNumber: string;
}

export interface IInboundPackageDetailData {
  currentLocation: ICurrentLocation;
  dealSummary: IDealSummary;
  inventoryHistory: IInventoryHistory[];
}

export enum TPackageSourceType {
  NL_STAFF = 'NL_STAFF',
  DEALER = 'DEALER',
  RETAILER = 'RETAILER',
  HOME_PICKUP = 'HOME_PICKUP',
}

// Package details type
export interface ISourceData {
  address: string;
  contactNumber: string;
  name: string;
  nlStaffId: string;
  nlStaffName: string;
  storeId: string;
  storeName: string;
  type: TPackageSourceType;
}

export enum TPackageInvoiceType {
  SPOT_SALE_TO_UNDERWRITER = 'SPOT_SALE_TO_UNDERWRITER',
  COURIER_FEE = 'COURIER_FEE',
  DISPUTE_PAY_DIFFERENCE_CREDIT_NOTE = 'DISPUTE_PAY_DIFFERENCE_CREDIT_NOTE',
  DISPUTE_RETURN_COURIER_FEE_CREDIT_NOTE = 'DISPUTE_RETURN_COURIER_FEE_CREDIT_NOTE',
  DISPUTE_RETURN_SELL_CREDIT_NOTE = 'DISPUTE_RETURN_SELL_CREDIT_NOTE',
}

export interface IInvoiceDetails {
  _id: string;
  tenantId: string;
  invoiceType: TPackageInvoiceType;
  invoiceUrl: string;
  appraisalCode: string;
  transactionType: string;
}

/**
 * The inventory status in the system.
 * MISSING: This is its initial state when a discrepancy is first identified.
 * FOUND: A previously 'MISSING' item has been located and reconciled with inventory.
 */
export type TInventoryStatus = 'MISSING' | 'FOUND';

export interface IPackageDetailsData {
  _id: string;
  appraisalCode: string;
  assetName: string;
  awbTrackingURL: string;
  awbNumber: string;
  barCode: string;
  boxNumber: string;
  brandName: string;
  categoryName: string;
  courierFees: {
    [key: string]: number;
  };
  createdBy: string;
  createdDate: number;
  courierProvider: string;
  imeiOrSerialNumber: string;
  inboundType: string;
  invoiceList: IInvoiceDetails[];
  inventoryLocation: string;
  inventoryTransactionId: string;
  inboundScanCode: string;
  isInventoryInBlackBox: boolean;
  missingInventoryStatus?: TInventoryStatus;
  podId: string;
  podInventoryEntryTimeStamp: number;
  podName: string;
  podNumber: string;
  podType: TPodType;
  purchaseValue: number;
  purchasedGrade: string;
  sellGrade: string;
  sourceOfSupply: ISourceData;
  trayEntryTimeStamp: number;
  trayId: string;
  trayNumber: string;
  assetStorage?: string;
  paymentType?: string;
  updatedDate?: number;
}

export interface IPackageDetailsResponse {
  data: IPackageDetailsData;
  status: boolean;
}

// Pendency screen types
export interface IPendencyDetailsPayload {
  limit?: number;
  page?: number;
  filterKey?: string;
  inflowFilterType?: string;
  isToday: boolean;
  podType?: TPodType | null;
}

export interface IPodInfo {
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  podDescription: string;
  podName: string;
  podNumber: string;
  podType: TPackagePodType;
  status: boolean;
  tenantId: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface IPodStat {
  count: number;
  podInfo: IPodInfo;
}

export interface InventorySourceDetails {
  storeName: string;
}

export enum InventoryStatus {
  COURIER_INIT = 'COURIER-INIT',
  COURIER_PICKED_UP = 'COURIER-PICKEDUP',
  COURIER_DONE = 'COURIER-DONE',
  COURIER_CANCELLED = 'COURIER-CANCELLED',
  COURIER_FAILED = 'COURIER-FAILED',
  COURIER_RETURNED = 'COURIER-RETURNED',
  COURIER_NOT_DELIVERED = 'COURIER-NOT-DELIVERED',
  PICKUP_STARTED = 'PICKUP-STARTED',
  COURIER_OUT_FOR_DELIVERY = 'COURIER-OUT-FOR-DELIVERY',
  COURIER_SCHEDULED_FOR_RETURN = 'COURIER-SCHEDULED-FOR-RETURN',
  COURIER_SHELVED = 'COURIER-SHELVED',
  COURIER_PICK_UP_FAILED = 'COURIER-PICK-UP-FAILED',
}

export interface IInventory {
  appraisalCode: string;
  assetName: string;
  awbNumber: string;
  barCode: string;
  courierStatus: InventoryStatus;
  createdDate: number;
  courierProvider: string;
  storageProviderStoreName: string;
  sourceOfSupply: InventorySourceDetails;
}

export interface Inventories {
  inventories: IInventory[];
  total: number;
}

/**
 * This interface represents the data structure for the pendency details, but
 * the BE is not sending the complete data, so we have to handle the
 * updatingData state separately.
 */
export interface IPendencyDetailsDataUpdate {
  updatingData?: boolean;
}

export interface IPendencyDetailsData {
  inventories: Inventories;
  podStats: IPodStat[];
  errorCode?: string;
  aging: IReportAging[];
  updatingData?: boolean;
}

export interface IPendencyDetailsResponse {
  data: IPendencyDetailsData | IPendencyDetailsDataUpdate;
  errorCode?: string;
  status: boolean;
}

/**
 * Payload format for scanning a package to remove.
 */
export interface ISearchInHouseInventoriesPayload {
  packageScannedType: string;
  inventoryScanCode: string;
}

/**
 * Data that we expect after scanning an in house package
 */
export interface ISearchInHouseInventoriesData {
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  awbNumber: string | undefined;
  barCode: string;
  boxNumber: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  courierProvider: string;
  createdBy: string;
  createdDate: number;
  inventoryLocation: string;
  inventoryTransactionId: string;
  isEligibleForPodSwitch?: boolean;
  isInventoryInBlackBox: boolean;
  podId: string;
  podName: string;
  podNumber: string;
  podType: TPodType;
  trayId: string;
  trayNumber: string;
  updatedBy: string;
  updatedDate: number;
}

/**
 * Response data format we expect from scanning a package to remove.
 */
export interface ISearchInHouseInventoriesResponse {
  success: boolean;
  data: ISearchInHouseInventoriesData;
}

/// Weekly Stats types
export interface IWeeklyStatsInfo {
  percentage: number;
  total: number;
}

export enum IWeeklyStatsType {
  EVALUATION = 'EVALUATION',
  INBOUND = 'INBOUND',
  OUTBOUND = 'OUTBOUND',
  TRANSFER = 'TRANSFER',
}

export interface IWeeklyStatsInfoData {
  key: IWeeklyStatsType;
  value: IWeeklyStatsInfo;
}

export interface IWeeklyStatsInfoResponse {
  data: IWeeklyStatsInfoData[];
  message: string;
  status: boolean;
}

/**
 * This is the data structure for the Courier Information.
 */
export interface ICourierInfo {
  _id: string;
  courierPersonFullName: string;
  courierPersonIdentityImages?: IFileUploadData[];
  courierProvider: string;
  courierSignature?: IFileUploadData[];
  gatePass: string;
  inboundRemarks: string;
  isNorthLadderCourier: boolean;
  nlStaffId: string;
  nlStaffName: string;
  transactionStatus: 'OPEN' | 'CLOSED';
}
export interface ICourierInfoResponse {
  data: ICourierInfo;
  status: boolean;
}

/**
 * This is the payload for updating the courier driver information.
 */
export interface IUpdateCourierDriverInfoPayload {
  inboundId: string;
  courierPersonIdentityImages?: IInboundSecureFile[];
  inboundRemarks?: string;
}

/**
 * This is the response after updating the courier driver information.
 */
export interface IUpdateCourierDriverInfoResponse {
  message: string;
  status: boolean;
}

/**
 * This is the payload for getting the driver attachment files from CDN using the signed URL.
 */
export interface IGetFileFromCdnUrlPayload {
  filePath: string;
}

/**
 * This is the response payload after getting the driver attachment files from
 * CDN using the signed URL.
 */
export interface IGetFileFromCdnUrlResponse {
  data: string;
  message: string;
  status: boolean;
}

/**
 * Payload for archiving an inbound transaction.
 */
export interface IArchiveInboundTransactionPayload {
  inboundId: string;
}

/**
 * The RAW response data format we expect from archiving an inbound transaction.
 */
export interface IArchiveInboundTransactionResponse {
  data: null;
  message: string;
  status: boolean;
}

/**
 * Response structure for getting the inventories based on a zone.
 */
export interface IInboundZoneInventoriesListData {
  inventories: IInventoryInfo[];
  total: number;
}

/**
 * This interface represents the response format for inventory list based on zone.
 */
export interface IInboundZoneInventoryListResponse {
  status: boolean;
  data: IInboundZoneInventoriesListData;
  message: string;
}

/**
 * Enum for the different types of inventory zones.
 */
export enum IInboundInventoryZoneType {
  BLACK_BOX = 'BLACK_BOX',
  EVALUATION = 'EVALUATION',
  HAND_OVER = 'HAND_OVER',
  INBOUND = 'INBOUND',
  INVENTORY = 'INVENTORY',
  OUTBOUND = 'OUTBOUND',
}

/**
 * Raw response data format for list of inventories for a zone.
 */
export interface IGetInboundZoneInventoriesPayload {
  inventoryTransactionId: string;
  limit: number;
  page: number;
  zone: IInboundInventoryZoneType;
}

export interface IInitialLocationsItem {
  key: string;
  i18n: TTranslatedText;
}

export interface IInitialLocationsResponse {
  status: boolean;
  data: IInitialLocationsItem[];
}

/// Tray name list api types
export interface IGetTrayNameListPayload {
  trayLocation: string;
  noOfTrays: number;
}

export interface IGetTrayNameListData {
  trays: string[];
  uniqueId: string;
}

export interface IGetTrayNameListResponse {
  status: boolean;
  message: string;
  data: IGetTrayNameListData;
}

// Create tray
export interface ICreateNewTraysPayload {
  uniqueId: string;
}

export interface ICreateTrayItem {
  trayNumber: string;
  trayName: string;
}

export interface ICreateTraysData {
  trays: ICreateTrayItem[];
}

export interface ICreateTraysResponse {
  status: boolean;
  message: string;
  data: ICreateTraysData;
}

export interface ISearchTraysResultPayload {
  filter?: string;
  limit: number;
  page?: number;
  search?: string;
}

export interface ISearchTransferResultPayload {
  filter?: string;
  inventoryTransferFilterType?: string;
  limit?: number;
  page?: number;
  search?: string;
}
// Tray list types

/**
 * `TTrayOperationalStatus` is type which defines the type of trayOperationalStatus.
 */
export type TTrayOperationalStatus =
  | 'INBOUND'
  | 'EVALUATION'
  | 'BLACK_BOX'
  | 'DISPATCH'
  | 'OUTBOUND'
  | 'OUTBOUND'
  | 'INVENTORY'
  | 'EMPTY'
  | 'MISSING'
  | 'RECOVERED'
  | 'WIP';

export interface ITrayItem {
  __v: number;
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  currentLocation?: TPackagePodType;
  initialLocation?: TPackagePodType;
  inventoryCount: number;
  podType: TPodType;
  status: boolean;
  tenantId: string;
  trayName: string;
  trayNumber: string;
  trayOperationalStatus?: TTrayOperationalStatus;
  trayTransferStatus: 'IN_PROGRESS' | 'RECEIVED';
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface ITayListData {
  trays: ITrayItem[];
  total: number;
  receivedCount: number;
  pendingCount: number;
  missingCount: number;
}

export interface ITayListRawData {
  trays: ITrayItem[];
  total: number;
  trayTransferStatus: {
    count: number;
    trayTransferStatus: 'IN_PROGRESS' | 'RECEIVED' | 'MISSING';
  }[];
}

/**
 * The data structure for the Tray Number Filter Item.
 */
export interface ITrayFilterItem {
  appraisalCount: string;
  countColor: unknown;
  isSelected?: boolean;
  key: string;
  trayColor: unknown;
  trayId?: string;
  trayNumber: string;
}

export interface ITayListResponse {
  status: boolean;
  data: ITayListRawData;
}

export interface ITrayFilterItemLocalized {
  type: string;
  count: number;
  i18nLabel: TTranslatedText;
  i18nDescription?: TTranslatedText;
}

export interface TTrayFiltersResponse {
  status: boolean;
  data: ITrayFilterItemLocalized[];
}

/// Tray details types
export interface ITrayAgeingInfo {
  key: string;
  fromHour: number;
  toHour?: number;
  count: number;
}

export interface InventoryTransferInfo {
  destinationPod: TPodType;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: TPackagePodType;
  sourcePod: TPodType;
  sourceZone: TPackagePodType;
}

export interface ITrayInfo {
  _id: string;
  trayNumber: string;
  trayName: string;
  tenantId: string;
  inventoryTransferId: string;
  status: boolean;
  createdBy: string;
  createdUserId: string;
  createdDate: number;
  updatedUserId: string;
  updatedDate: number;
  currentLocation: TPackagePodType;
  initialLocation?: TPackagePodType;
  inventoryCurrentLocation: string;
  lastInventoryEntryTimeStamp: number;
  trayOperationalStatus: string;
  trayTransferStatus: string;
  inventoryCount: number;
  inventoryTransferInfo: ITransferMissingTrayInventoryTransferInfo;
  podName: string;
  podId: string;
  podNumber: string;
  podType: TPodType;
  trayEntryTimeStamp: number;
  trayEntryTimeStampLast: number;
  trayUpdatedBy: string;
  updatedBy: string;
}

export interface ITrayDetailsData {
  trayInfo: ITrayInfo;
  trayAgeingInfo: ITrayAgeingInfo[];
}

export interface ITrayDetailsResponse {
  status: boolean;
  data: ITrayDetailsData;
}

export interface ISearchTrayPackagesPayload {
  ageFilter?: string;
  limit: number;
  page?: number;
  search?: string;
  trayId: string;
}

// tray details inventories
export interface ITrayDetailsPackagesData {
  inventories: IPackageDetailsData[];
  total: number;
}

export interface ITrayDetailsPackagesResponse {
  status: boolean;
  data: ITrayDetailsPackagesData;
}

export interface ILastTrayDetailsData {
  __v: number;
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  currentLocation: TPackagePodType;
  initialLocation: TPackagePodType;
  podId: string;
  podName: string;
  podNumber: string;
  podType: TPodType;
  status: boolean;
  tenantId: string;
  trayName: string;
  trayNumber: string;
  trayOperationalStatus: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface ILastTrayDetailsResponse {
  status: boolean;
  data: ILastTrayDetailsData;
}

export interface IPodLocationsPayload {
  inventoryId: string;
}

export interface IPodsLocationResponse {
  _id: string;
  podNumber: string;
  podName: string;
  podDescription: string;
  podType: TPodType;
  zone: string;
}

/**
 * This is the response type for the Pod Location list API
 */
export interface IPodsLocationResponseRaw {
  data: IPodsLocationResponse[];
  status: boolean;
}

/**
 * Payload for empty a tray.
 */
export interface IEmptyTrayPayload {
  inventoryTransferId: string;
  trayId: string;
  trayNumber: string;
}

/**
 * The RAW response data format we expect from empty tray api.
 */
export interface IEmptyTrayResponse {
  data: {
    isTrayEmpty: boolean;
  };
  message: string;
  status: boolean;
}

/**
 * Payload format for scanning a package to switch pod.
 */
export interface IScanPackageToSwitchPodPayload {
  inventoryId: string;
  inventoryScanCode: string;
  packageScannedType?: string;
  podId: string;
  remark?: string;
}

/**
 * Response data format we expect from scanning to switch pod.
 */
export interface IScanPackageToSwitchPodData {
  inventoryData: IPackageDetailsData;
  packageIdentifier: string;
  podInfo: {
    podDescription: string;
    podName: string;
    podNumber: string;
    podType: TPodType;
  };
  trayInfo?: ITransferTrayInfo;
}

/**
 * Response format for scanning a package to switch pod.
 */
export interface IScanPackageToSwitchPodResponse {
  data: IScanPackageToSwitchPodData;
  message: string;
  status: boolean;
}

/**
 * Payload for archiving an inbound transaction.
 */
export interface IMarkTrayAsMissingPayload {
  trayNumber: string;
  inventoryTransferId: string;
}

/**
 * The RAW response data format we expect from mark tray as missing api.
 */
export interface IMarkTrayMissingResponse {
  data: null;
  message: string;
  status: boolean;
}

export enum IInventoryTransferStatus {
  OPEN = 'OPEN', // Source scanned
  IN_PROGRESS = 'IN_PROGRESS', // Destination scanned
  CLOSED = 'CLOSED', // Done
  CANCELLED = 'CANCELLED', // Cancelled
  CANCELLATION_IN_PROGRESS = 'CANCELLATION_IN_PROGRESS',
}

/**
 * This interface represents the inventory transfer list data.
 */
export interface IInventoryTransferList {
  __v: number;
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: IInventoryTransferStatus;
  missingTrays: number;
  noOfTrays: number;
  sourcePod: TPodType;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface IInventoryTransferListData {
  inventoryTransferList: IInventoryTransferList[];
  totalCount: number;
}

export interface IInventoryTransferListResponse {
  data: IInventoryTransferListData;
  status: boolean;
}

/**
 * Transfer filter options types
 */
export interface ITransferFilterOption {
  destinationZone: string;
  key: string;
  sourceZone: string;
  value: TTextLocalized;
}

export interface ITransferFilterOptionsResponse {
  status: boolean;
  data: ITransferFilterOption[];
  message: string;
}

/**
 * Transfer details types
 */
export interface ITransferDetailsData {
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: IInventoryTransferStatus;
  missingTrays: number;
  noOfTrays: number;
  sourceIsCommonStation: boolean;
  sourcePod: string;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceWorkStationId: string;
  sourceWorkStationName: string;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
  trays: ITransferTray[];
  formattedTrays: ITray[];
  scannedDetails?: IBaseTransferDetails;
}

export interface ITransferDetailsResponse {
  data: ITransferDetailsData;
  message: string;
  status: boolean;
}

export interface ISearchTransferPackagesPayload {
  limit: number;
  page?: number;
  trayId?: string;
  inventoryTransferId: string;
}

/**
 * This is the data structure of Source of Supply object.
 */
interface ISourceOfSupply {
  _id: string;
  address: string;
  contactNumber: string;
  name: string;
  nlStaffId: string;
  nlStaffName: string;
  storeId: string;
  storeName: string;
  type: string;
}

export interface ITransferPackage {
  __v: number;
  _id: string;
  appraisalCode: string;
  assetId: string;
  assetName: string;
  awbNumber?: string;
  barCode: string;
  blackBoxMoveReason: any;
  boxNumber: any;
  boxScannedBy: string;
  boxScannedDate: string;
  boxScannedUserId: string;
  brandId: string;
  brandName: string;
  categoryId: string;
  categoryName: string;
  courierProvider: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  description: string;
  groupId: string;
  groupName: string;
  historyType: string;
  imeiOrSerialNumber: string;
  inboundDate: number;
  inboundPackageScannedType: string;
  inboundScanCode: string;
  inboundType: string;
  inventoryId: string;
  inventoryLocation: string;
  inventoryTransactionId: string;
  inventoryTransferId: string;
  inventoryTransferStatus: string;
  isCommonStation: boolean;
  isInventoryInBlackBox: boolean;
  packageScannedType: string;
  podId: string;
  podInventoryEntryTimeStamp: number;
  podName: string;
  podNumber: string;
  podType: string;
  sourceOfSupply: ISourceOfSupply;
  tenantId: string;
  transactionStatus: string;
  trayEntryTimeStamp: number;
  trayId: string;
  trayNumber: string;
  trayOperationalStatus: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
  workStationId: any;
  workStationName: any;
}

export interface ITransferTray {
  trayId: string;
  quantity: number;
  tray: string;
  trayOperationalStatus?: string;
}

export interface ITransferDetailsPackagesData {
  inventory: ITransferPackage[];
  total: number;
}

export interface ITransferDetailsPackagesResponse {
  data: ITransferDetailsPackagesData;
  status: boolean;
}

/**
 * Transfer Stats Types
 */
export interface ITransferStatsOldestOpenTransfer {
  _id: string;
  createdBy: string;
  createdDate: number;
  destinationZone: string;
  inventoryTransferId: string;
  inventoryTransferStatus: string;
  sourceZone: string;
}

export interface ITransferStatsOldestMissingTray {
  _id: string;
  currentLocation: string;
  initialLocation: string;
  inventoryTransferId: string;
  lastInventoryEntryTimeStamp: number;
  missingInventoriesCount: number;
  trayNumber: string;
  trayOperationalStatus: string;
  trayTransferStatus: string;
  updatedDate: number;
}

export interface ITransferStatsData {
  oldestMissingTray: ITransferStatsOldestMissingTray;
  oldestOpenTransfer: ITransferStatsOldestOpenTransfer;
  openTransfers: number;
  totalMissingTrays: number;
}

export interface ITransferStatsResponse {
  data: ITransferStatsData;
  status: boolean;
}

/**
 * Transfer Missing Trays Types
 */
export interface ITransferMissingTrayInventoryTransferInfo {
  __v: number;
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: string;
  missingTrays: number;
  noOfTrays: number;
  sourceIsCommonStation: boolean;
  sourcePod: string;
  sourceTrayId: string | null;
  sourceTrayNumber: string | null;
  sourceWorkStationId: string | null;
  sourceWorkStationName: string | null;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface ITransferMissingTray {
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  currentLocation: string;
  initialLocation: string;
  inventoryCount: number;
  inventoryCurrentLocation: string;
  inventoryTransferId: string;
  inventoryTransferInfo: ITransferMissingTrayInventoryTransferInfo;
  lastInventoryEntryTimeStamp: number;
  missingTrayLabel: string;
  status: boolean;
  tenantId: string;
  trayName: string;
  trayNumber: string;
  trayOperationalStatus: string;
  trayTransferStatus: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface ITransferMissingTraysData {
  total: number;
  trays: ITransferMissingTray[];
}

export interface ITransferMissingTraysResponse {
  data: ITransferMissingTraysData;
  status: boolean;
}

export interface IUseTransferMissingTraysQueryParams {
  limit?: number;
  page?: number;
  search?: string;
  inventoryTransferId?: string;
}

/**
 * Payload for scanning a pod or tray code.
 */
export interface IScanPodOrTrayCodePayload {
  scanCode: string;
}

/**
 * Tray pod scan response types
 */
export interface IInventoryTransfer {
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: string;
  missingTrays: number;
  noOfTrays: number;
  sourcePod: string;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface IWorkStation {
  _id: string;
  commonStation: boolean;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  status: boolean;
  tenantId: string;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
  workStationId: string;
  workStationName: string;
  zoneType: string;
}

export enum InventoryTransferError {
  DESTINATION_WORKSTATION_NOT_FOUND = 'DESTINATION_WORKSTATION_NOT_FOUND',
  DESTINATION_POD_NOT_FOUND = 'DESTINATION_POD_NOT_FOUND',
  SOURCE_TRAY_OR_POD_NOT_FOUND = 'SOURCE_TRAY_OR_POD_NOT_FOUND',
  SOURCE_WORKSTATION_NOT_FOUND = 'SOURCE_WORKSTATION_NOT_FOUND',
  SOURCE_POD_NOT_FOUND = 'SOURCE_POD_NOT_FOUND',
  SINGLE_TRAY_WORK_IN_PROGRESS = 'SINGLE_TRAY_WORK_IN_PROGRESS',
  MULTIPLE_TRAY_WORK_IN_PROGRESS = 'MULTIPLE_TRAY_WORK_IN_PROGRESS',
  MULTIPLE_OPEN_INBOUND_TRANSACTIONS = 'MULTIPLE_OPEN_INBOUND_TRANSACTIONS',
  SINGLE_OPEN_INBOUND_TRANSACTION = 'SINGLE_OPEN_INBOUND_TRANSACTION',
  SINGLE_ACTIVE_INVENTORY_TRANSFER = 'SINGLE_ACTIVE_INVENTORY_TRANSFER',
  MULTIPLE_ACTIVE_INVENTORY_TRANSFERS = 'MULTIPLE_ACTIVE_INVENTORY_TRANSFERS',
  MULTIPLE_POD_TYPES_NOT_ALLOWED = 'MULTIPLE_POD_TYPES_NOT_ALLOWED',
  ALL_TRAYS_MISSING = 'ALL_TRAYS_MISSING',
}

/**
 * This interface defines the structure of the Inbound Item.
 */
export interface IOpenInboundAssetItem {
  _id: string;
  courierPersonFullName: string;
  courierProvider: string;
  inventoryCount: number;
  inventoryTransactionId: string;
  transactionStatus: 'OPEN' | 'CLOSED';
  updatedBy: string;
  updatedDate: number;
}

export interface IScanPodOrTrayCodeErrorResponse {
  openInboundTransactionCount?: number;
  activeTransferDetails?: IInventoryTransferList;
  inboundTransactionDetails?: IOpenInboundAssetItem;
  trayInformation?: {
    trayName: string;
  };
  errorCode: InventoryTransferError;
}

export interface IScanPodOrTrayCodeData {
  inventoryTransfer: IInventoryTransfer;
  inventoryTransferId: string;
  noOfTrays: number;
  possibleDestinationPod: string;
  possibleDestinationZone: string;
  totalInventories: number;
  workStation: IWorkStation;
}

export interface IScanPodOrTrayCodeResponse {
  data: IScanPodOrTrayCodeData | IScanPodOrTrayCodeErrorResponse;
}

export interface IGetTransferTraysParams {
  filter?: string;
  inventoryTransferId: string;
  limit?: number;
  page?: number;
}

/**
 * Interface for marking a tray as received payload
 */
export interface IMarkTrayAsReceivedPayload {
  inventoryTransferId: string;
  trayNumber: string;
}

/**
 * Interface for marking a tray as received response
 */
export interface IMarkTrayAsReceivedResponse {
  message: string;
  status: boolean;
}

/**
 * Interface for scanning destination zone params
 */
export interface IScanDestinationZoneParams {
  inventoryTransferId: string;
  scannedPodOrWs: string;
}

export interface IScanDestinationZoneSuccessResponse {
  status: boolean;
  data: IScanDestinationZoneData;
}

/**
 * Interface for scanning destination zone response
 */
export interface IScanDestinationZoneResponse {
  data: IScanDestinationZoneSuccessResponse | IScanPodOrTrayCodeErrorResponse;
}

/**
 * Interface for scanning destination zone response
 */
export interface IScanDestinationZoneData {
  type: 'SUCCESS';
  inventoryTransfer: IInventoryTransfer;
  inventoryTransferCancelled: boolean;
  inventoryTransferClosed: boolean;
  inventoryTransferInProgress: boolean;
  podInfo: null; // TODO: @renji Why do we need this key.
  workStation: IWorkStation;
}

export interface IScanDestinationZoneError
  extends IScanPodOrTrayCodeErrorResponse {
  type: 'FAILED';
}

export type TScanDestinationZoneDataOrError =
  | IScanDestinationZoneData
  | IScanDestinationZoneError;

/**
 * Interface for confirming transfer params
 */
export interface IConfirmTransferPayload {
  inventoryTransferId: string;
}

/**
 * Interface for marking a tray as received response
 */
export interface IConfirmTransferResponse {
  message: string;
  status: boolean;
}

/**
 * All Transfer pendency list data type
 */
export interface ITransferPendenciesData {
  key: string;
  value: TTextLocalized;
  sourceZone: string;
  destinationZone: string;
  totalInventories: number;
  totalTrays: number;
}

export interface ITransferPendenciesResponse {
  status: boolean;
  data: ITransferPendenciesData[];
  message: string;
}

// Pendency details payload
export interface IPendencyInventoryDetailsPayload {
  limit: number;
  page?: number;
  trayId?: string;
  inventoryTransferType: string;
}

// Pendency details inventories
export interface IPendenciesPackagesData {
  inventories: IPackageDetailsData[];
  total: number;
}

export interface IPendenciesPackagesResponse {
  status: boolean;
  data: IPendenciesPackagesData;
}

/**
 * Response format for scanning a tray pod transfer cancel.
 */
export interface IScanTrayPodTransferCancelResponse {
  status: boolean;
  data: {
    doneCancelation: boolean;
  };
}

/**
 * Payload format for scanning a tray pod transfer cancel.
 */
export interface IScanTrayPodTransferCancelPayload {
  scanCode: string;
  inventoryTransferId: string;
}

/**
 * Payload format for scanning a tray pod transfer cancel.
 */
export interface IInitiateCancelTransferPayload {
  inventoryTransferId: string;
}

export interface IUseTransferPendenciesTraysQueryParams {
  inventoryTransferFilterType: string;
}

/**
 * Interface representing the Tray details.
 */
export interface ITrayInfoItem {
  trayNumber: string;
  appraisalCount: string;
}

/**
 * Interface for transfer pendencies tray
 */
export interface ITransferPendenciesTray {
  totalInventories: string;
  trayId: string;
  trayNumber: string;
}

/**
 * Interface for transfer pendencies trays response
 */
export interface ITransferPendenciesTraysResponse {
  data: ITransferPendenciesTray[];
  status: boolean;
}

/**
 * Interface for base transfer details
 */
export interface IBaseTransferDetails {
  barcode: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferId: string;
  isDestinationScanned: boolean;
  isPod: boolean;
  packagesCount: number;
  sourcePod: string;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceZone: string;
  title: string;
  transferStatus: string;
  traysCount?: number;
  workStationId: string;
  workStationProcessId: string;
  workStationZoneType: string;
}

export interface ICancelTransferDetails {
  _id: string;
  createdBy: string;
  createdDate: number;
  createdUserId: string;
  destinationIsCommonStation: boolean;
  destinationPod: string;
  destinationWorkStationId: string;
  destinationWorkStationName: string;
  destinationZone: string;
  inventoryTransferClosingDate: number;
  inventoryTransferId: string;
  inventoryTransferStatus: IInventoryTransferStatus;
  missingTrays: number;
  noOfTrays: number;
  sourceIsCommonStation: boolean;
  sourcePod: string;
  sourceTrayId: string;
  sourceTrayNumber: string;
  sourceWorkStationId: any;
  sourceWorkStationName: any;
  sourceZone: string;
  tenantId: string;
  totalInventories: number;
  updatedBy: string;
  updatedDate: number;
  updatedUserId: string;
}

export interface ICancelScanData {
  inventoryTransfer: ICancelTransferDetails;
  inventoryTransferCancelled: boolean;
  inventoryTransferClosed: boolean;
  inventoryTransferInProgress: boolean;
}

export interface ICancelScanRawResponse {
  data: ICancelScanData | IScanPodOrTrayCodeErrorResponse;
}

export interface ICancelScanResponse {
  data: ICancelScanData;
  status: boolean;
}

export interface IFetchTransferDetailsParams {
  inventoryTransferId: string;
  isConvertToTransferDetails?: boolean;
  isTrays: boolean;
  refetchId?: string;
}

export interface ITransferStatsParams {
  inventoryTransferId?: string;
}

// Enhanced error type that includes custom error codes
export interface ICustomApiError extends IApiError {
  data?: {
    errorCode?: InventoryTransferError;
    errorData?: IScanPodOrTrayCodeErrorResponse;
  };
}
